import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { MerchantAuthProvider } from './contexts/MerchantAuthContext';
import { SupportAuthProvider } from './contexts/SupportAuthContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { TraderProtectedRoute } from './components/TraderProtectedRoute';
import { MerchantProtectedRoute } from './components/MerchantProtectedRoute';
import { SupportProtectedRoute } from './components/SupportProtectedRoute';
import { AdminProtectedRoute } from './components/AdminProtectedRoute';
import { AnalyticsRouteTracker } from './components/AnalyticsRouteTracker';
import { PageThemeToggle } from './components/ThemeToggle';

// Страницы авторизации
import { TraderAuthPage } from './pages/TraderAuthPage';
import { MerchantAuthPage } from './pages/MerchantAuthPage';
import { SupportAuthPage } from './pages/SupportAuthPage';
import { AdminAuthPage } from './pages/AdminAuthPage';

// Дашборды
import { TraderDashboardPage } from './pages/TraderDashboardPage';
import { TraderImpersonatePage } from './pages/TraderImpersonatePage';
import { TraderAppealsPage } from './pages/TraderAppealsPage';
import { MerchantLayout } from './components/MerchantLayout';
import { MerchantIntegrationPage } from './pages/MerchantIntegrationPage';
import { MerchantSettingsPage } from './pages/MerchantSettingsPage';
import { MerchantDashboardPage } from './pages/MerchantDashboardPage';
import { MerchantImpersonatePage } from './pages/MerchantImpersonatePage';
import { MerchantSandboxPage } from './pages/MerchantSandboxPage';
import { MerchantPaymentHistoryPage } from './pages/MerchantPaymentHistoryPage';
import { MerchantTopupsHistoryPage } from './pages/MerchantTopupsHistoryPage';
import { SupportDashboardPage } from './pages/SupportDashboardPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { AdminConversionPage } from './pages/AdminConversionPage';

// Публичные страницы
import { PaymentDetailsPage } from './pages/PaymentDetailsPage';
import { MerchantAPITestPage } from './pages/MerchantAPITestPage';
import { MerchantAppealTestPage } from './pages/MerchantAppealTestPage';

import { InviteCompletePage } from './pages/InviteCompletePage';
import { PasswordResetPage } from './pages/PasswordResetPage';
import { cabinetPath, currentCabinetRole } from './lib/domains';


function App() {
    const cabinetRole = currentCabinetRole();

    return (
        <AuthProvider>
            <MerchantAuthProvider>
                <SupportAuthProvider>
                    <AdminAuthProvider>
                        <Router>
                            <AnalyticsRouteTracker />
                            <PageThemeToggle />
                            <Routes>
                                {cabinetRole === 'trader' && <>
                                    <Route path={cabinetPath('trader', '/')} element={<Navigate to={cabinetPath('trader', '/dashboard')} replace />} />
                                    <Route path={cabinetPath('trader', '/login')} element={<TraderAuthPage mode="login" />} />
                                    <Route path={cabinetPath('trader', '/impersonate')} element={<TraderImpersonatePage />} />
                                    <Route path={cabinetPath('trader', '/dashboard')} element={<TraderProtectedRoute><TraderDashboardPage /></TraderProtectedRoute>} />
                                    <Route path={cabinetPath('trader', '/appeals')} element={<TraderProtectedRoute><TraderAppealsPage /></TraderProtectedRoute>} />
                                </>}
                                {cabinetRole === 'merchant' && <>
                                    <Route path={cabinetPath('merchant', '/')} element={<Navigate to={cabinetPath('merchant', '/dashboard')} replace />} />
                                    <Route path={cabinetPath('merchant', '/login')} element={<MerchantAuthPage mode="login" />} />
                                    <Route path={cabinetPath('merchant', '/impersonate')} element={<MerchantImpersonatePage />} />
                                    <Route element={<MerchantProtectedRoute><MerchantLayout /></MerchantProtectedRoute>}>
                                        <Route path={cabinetPath('merchant', '/dashboard')} element={<MerchantDashboardPage />} />
                                        <Route path={cabinetPath('merchant', '/payments-history')} element={<MerchantPaymentHistoryPage />} />
                                        <Route path={cabinetPath('merchant', '/topups-history')} element={<MerchantTopupsHistoryPage />} />
                                        <Route path={cabinetPath('merchant', '/integration')} element={<MerchantIntegrationPage />} />
                                        <Route path={cabinetPath('merchant', '/settings')} element={<MerchantSettingsPage />} />
                                        <Route path={cabinetPath('merchant', '/sandbox')} element={<MerchantSandboxPage />} />
                                        <Route path={cabinetPath('merchant', '/api-test')} element={<MerchantAPITestPage />} />
                                        <Route path={cabinetPath('merchant', '/appeal-test')} element={<MerchantAppealTestPage />} />
                                    </Route>
                                </>}
                                {cabinetRole === 'support' && <>
                                    <Route path={cabinetPath('support', '/')} element={<Navigate to={cabinetPath('support', '/dashboard')} replace />} />
                                    <Route path={cabinetPath('support', '/login')} element={<SupportAuthPage mode="login" />} />
                                    <Route path={cabinetPath('support', '/dashboard')} element={<SupportProtectedRoute><SupportDashboardPage /></SupportProtectedRoute>} />
                                </>}
                                {cabinetRole === 'admin' && <>
                                    <Route path={cabinetPath('admin', '/')} element={<Navigate to={cabinetPath('admin', '/dashboard')} replace />} />
                                    <Route path={cabinetPath('admin', '/login')} element={<AdminAuthPage mode="login" />} />
                                    <Route path={cabinetPath('admin', '/dashboard')} element={<AdminProtectedRoute><AdminDashboardPage /></AdminProtectedRoute>} />
                                    <Route path={cabinetPath('admin', '/conversion')} element={<AdminProtectedRoute><AdminConversionPage /></AdminProtectedRoute>} />
                                </>}
                                {!cabinetRole && <>
                                    <Route path="/" element={<></>} />
                                    <Route path="/payment/:paymentId" element={<PaymentDetailsPage />} />
                                    <Route path="/invite/:token" element={<InviteCompletePage />} />
                                    <Route path="/password-reset/:token" element={<PasswordResetPage />} />
                                </>}
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Routes>
                        </Router>
                    </AdminAuthProvider>
                </SupportAuthProvider>
            </MerchantAuthProvider>
        </AuthProvider>
    );
}

export default App;
